import { Building2, Linkedin, Target, Users } from "lucide-react";
import { Link } from "react-router";
import { getTranslationFn, type Locale } from "../../i18n";
import { MarketingCta } from "./marketing-cta";
import { MarketingLayout } from "./marketing-layout";

interface AboutPageProps {
  locale: Locale;
}

const leadership = [
  {
    name: "Michał Jackowski",
    roleKey: "About.leadership.michal.role",
    linkedin: "https://www.linkedin.com/in/michal-jackowski-prof/",
  },
  {
    name: "Maciej Winnicki",
    roleKey: "About.leadership.maciej.role",
    linkedin: "https://www.linkedin.com/in/maciej-winnicki/",
  },
  {
    name: "Rafał Wiliński",
    roleKey: "About.leadership.rafal.role",
    linkedin: "https://www.linkedin.com/in/rafwilinski/",
  },
] as const;

export function AboutPage({ locale }: AboutPageProps) {
  const t = getTranslationFn(locale);

  return (
    <MarketingLayout locale={locale}>
      {/* Hero */}
      <section className="py-24 md:py-32">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-semibold tracking-tight text-slate-900 leading-[1.1]">
              {t("About.hero.title")}
            </h1>
            <p className="mt-6 text-xl text-slate-500 leading-relaxed font-light max-w-2xl mx-auto">
              {t("About.hero.description")}
            </p>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="pb-16 md:pb-24">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <Target className="w-6 h-6 text-brand-600" />
              <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">
                {t("About.mission.title")}
              </h2>
            </div>
            <p className="text-lg text-slate-500 leading-relaxed font-light">
              {t("About.mission.description")}
            </p>
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="pb-16 md:pb-24">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <Users className="w-6 h-6 text-brand-600" />
              <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">
                {t("About.leadership.title")}
              </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {leadership.map((person) => (
                <div
                  key={person.name}
                  className="p-6 rounded-2xl border border-slate-100"
                >
                  <h3 className="text-lg font-semibold text-slate-900">
                    {person.name}
                  </h3>
                  <p className="text-sm font-medium text-brand-600 mb-3">
                    {t(person.roleKey)}
                  </p>
                  <Link
                    to={person.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    <Linkedin className="w-4 h-4" />
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Company Details */}
      <section className="pb-24 md:pb-32">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <Building2 className="w-6 h-6 text-brand-600" />
              <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">
                {t("About.company.title")}
              </h2>
            </div>
            <div className="text-sm text-slate-500 leading-relaxed space-y-1">
              <p className="font-medium text-slate-900">
                {t("About.company.nameValue")}
              </p>
              <p>Pastelowa 4</p>
              <p>60-198 Poznań</p>
              <p>Polska</p>
              <p>
                {t("About.company.nip")}: {t("About.company.nipValue")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <MarketingCta locale={locale} />
    </MarketingLayout>
  );
}
